/**
 * Helper per scaricare l'immagine originale del badge di una certificazione.
 *
 * Le immagini dei badge sono servite da un'origine diversa (S3): l'attributo
 * `download` di un `<a>` viene ignorato dai browser per le risorse cross-origin,
 * quindi non basterebbe a forzare il salvataggio. Qui recuperiamo l'immagine come
 * blob e creiamo un object URL temporaneo su cui applicare `download`, così il
 * file viene salvato con un nome sensato (derivato dal nome della certificazione).
 *
 * Se il fetch fallisce (tipicamente per CORS) si ricade sull'apertura diretta
 * dell'URL in una nuova scheda, da cui l'utente può comunque salvare l'immagine.
 */

/** Estensione del file dedotta dal `Content-Type` o dall'URL (default: `png`). */
function inferExtension(url: string, contentType: string | null): string {
  const fromType = contentType?.split(';')[0].trim().toLowerCase();
  const typeMap: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
  };
  if (fromType !== undefined && typeMap[fromType] !== undefined) {
    return typeMap[fromType];
  }
  const pathname = (() => {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  })();
  const match = /\.([a-z0-9]+)$/i.exec(pathname);
  return match !== null ? match[1].toLowerCase() : 'png';
}

/** Trasforma un nome di certificazione in un nome file sicuro (senza estensione). */
function toSafeFileName(name: string): string {
  const normalized = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return normalized.length > 0 ? normalized : 'badge';
}

/** Avvia il download del blob con il nome file indicato. */
function triggerDownload(objectUrl: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Scarica l'immagine originale del badge. Il nome del file è derivato dal nome
 * della certificazione. Ritorna una promise che si risolve quando il download è
 * stato avviato (o è stato aperto il fallback in una nuova scheda).
 */
export async function downloadBadgeImage(
  imageUrl: string,
  certificationName: string,
): Promise<void> {
  const baseName = toSafeFileName(certificationName);
  try {
    const response = await fetch(imageUrl, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`Download non riuscito (HTTP ${response.status}).`);
    }
    const blob = await response.blob();
    const extension = inferExtension(imageUrl, response.headers.get('content-type'));
    const objectUrl = URL.createObjectURL(blob);
    try {
      triggerDownload(objectUrl, `${baseName}.${extension}`);
    } finally {
      // Rilascia l'object URL dopo un breve intervallo, così il click ha tempo
      // di essere elaborato dal browser prima della revoca.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
  } catch {
    // Fallback CORS: apre l'immagine in una nuova scheda per il salvataggio manuale.
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  }
}
